import { Typography } from '@pesto/ui';
import { Button } from '@pesto/ui/components/Button';
import { DetailRow } from '@pesto/ui/components/DetailRow';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { formatPhoneNumber } from '@pesto/utils';
import { ExternalLink } from 'lucide-react';

import type { ModalProps } from '../../types/modal.types';

import { type GetUserQuery, useGetUserQuery } from '../../../__generated__/graphql/api';
import { routes } from '../../../constants/routes';

export const UserQuickLookModalComponent = ({
  payload,
  api,
}: ModalProps<{ userId: string }, { user: GetUserQuery['user'] }>) => {
  const { userId } = payload;
  const { data, loading } = useGetUserQuery({ variables: { userId }, skip: !userId });

  const user = data?.user;
  const address = user?.billingAddress;

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Typography variant="body">That person could not be loaded.</Typography>;
  }

  const fullName = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ') || '—';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <DetailRow title="Name" value={fullName} copyEnabled={false} />
        <DetailRow title="Email" value={user.email ?? '—'} />
        <DetailRow title="Phone" value={user.phone ? formatPhoneNumber(user.phone) : '—'} copyEnabled={false} />
        <DetailRow title="Date of Birth" value={user.dob ?? '—'} copyEnabled={false} />
        <DetailRow title="User ID" value={user.id ?? '—'} />
        <DetailRow
          title="Billing Address"
          value={
            address
              ? [address.addressFirstLine, address.addressSecondLine, address.city, address.state, address.zip]
                  .filter(Boolean)
                  .join(', ')
              : '—'
          }
          copyEnabled={false}
        />
        <DetailRow title="Existing Loans" value={String(user.loans?.length ?? 0)} copyEnabled={false} />
      </div>

      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" asChild>
          <a href={`${routes.users}/${user.id}`} target="_blank" rel="noreferrer">
            <ExternalLink className="mr-1 h-4 w-4" /> Open full page
          </a>
        </Button>
        <Button type="button" onClick={() => api.close({ user })}>
          Use this person
        </Button>
      </div>
    </div>
  );
};
